import {
  type AverageRates,
  type Billing,
  type Hotels,
  type Rates,
  type RoomType,
} from '@entities/avail/dtos'
import { CacheAdapter } from '@shared/infra'
import _ from 'lodash'
import React, { createContext, useContext, useState } from 'react'

export interface IStoreFilterAfterAvail {
  hotelName?: string
  hotelAddress?: string
  price?: number[]
  filterOnlyAvailable?: boolean
  propertyTypeId?: string
  selectedCodes?: string[]
  selectedNeighborhoods?: string[]
  selectedIntegratedSystems?: string[]
  mealSelectedToFilter?: any
  withBreakfast?: boolean
  hotelChainSelected?: {
    value: string
    label: string
  }
}

// Interface do contexto para compartilhar os estados
interface IFilterContext {
  uniqueCodes: any[]
  setUniqueCodes: React.Dispatch<React.SetStateAction<any[]>>
  integratedSystems: any[]
  setIntegratedSystems: React.Dispatch<React.SetStateAction<any[]>>
  selectedIntegratedSystems: any[]
  setSelectedIntegratedSystems: React.Dispatch<React.SetStateAction<any[]>>
  filterOnlyAvailable: boolean
  setFilterOnlyAvailable: React.Dispatch<React.SetStateAction<boolean>>
  hotelName: string
  setHotelName: React.Dispatch<React.SetStateAction<string>>
  price: number[]
  setPrice: React.Dispatch<React.SetStateAction<number[]>>
  hotelAddress: string
  setHotelAddress: React.Dispatch<React.SetStateAction<string>>
  selectedCodes: any[]
  setSelectedCodes: React.Dispatch<React.SetStateAction<any[]>>
  selectedNeighborhoods: any[]
  setSelectedNeighborhoods: React.Dispatch<React.SetStateAction<any[]>>
  withBreakfast: boolean
  setWithBreakfast: React.Dispatch<React.SetStateAction<boolean>>
  hotelChainSelected: any
  setHotelChainSelected: React.Dispatch<React.SetStateAction<any>>
  mealIncluded: string[]
  setMealIncluded: React.Dispatch<React.SetStateAction<string[]>>
  propertyTypeId: string
  setPropertyTypeId: React.Dispatch<React.SetStateAction<string>>
  propertyTypeList: { id: string }[]
  setPropertyTypeList: React.Dispatch<React.SetStateAction<{ id: string }[]>>
  mealSelectedToFilter: any[]
  setMealSelectedToFilter: React.Dispatch<React.SetStateAction<any[]>>
  applyFilters: (cb: () => void, list: any) => void
  cleanFilters: (list: any, priceRange: number[]) => void
  findMealIncluded: (list: any) => void
  handleChangePrice: (value: number | number[]) => void
  handleCheckboxChange: ({
    checked,
    value,
  }: {
    checked: boolean
    value: string
  }) => void
  handleNeighborhoodsChange: ({
    checked,
    value,
  }: {
    checked: boolean
    value: string
  }) => void
  handleIntegratedSystemsChange: ({
    checked,
    value,
  }: {
    checked: boolean
    value: string
  }) => void
  filterAndSortPropertyTypes: (availList: Hotels[]) => void
  getAppliedFilter: () => void
  billings: (hotel: Hotels[]) => any[]
  systemIds: (hotels: Hotels[]) => string[]
}

// Criar contexto
const FilterContext = createContext<IFilterContext | undefined>(undefined)

// Hook para usar o contexto
export const useFilterContext = (): IFilterContext => {
  const context = useContext(FilterContext)
  if (!context) {
    throw new Error(
      'useFilterContext deve ser usado dentro de um FilterProvider',
    )
  }
  return context
}

// Provider do contexto
export function FilterProvider({ children }: { children: React.ReactNode }) {
  const [uniqueCodes, setUniqueCodes] = useState<any[]>([])
  const [integratedSystems, setIntegratedSystems] = useState<any[]>([])
  const [selectedIntegratedSystems, setSelectedIntegratedSystems] = useState<
    any[]
  >([])
  const [filterOnlyAvailable, setFilterOnlyAvailable] = useState<boolean>(false)
  const [hotelName, setHotelName] = useState<string>('')
  const [price, setPrice] = useState<number[]>([0, 0])
  const [hotelAddress, setHotelAddress] = useState<string>('')
  const [selectedCodes, setSelectedCodes] = useState<any[]>([])
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState<any[]>([])
  const [withBreakfast, setWithBreakfast] = useState<boolean>(false)
  const [hotelChainSelected, setHotelChainSelected] = useState<any>(null)
  const [mealIncluded, setMealIncluded] = useState<string[]>([])
  const [propertyTypeId, setPropertyTypeId] = useState<string>('')
  const [propertyTypeList, setPropertyTypeList] = useState<{ id: string }[]>([])
  const [mealSelectedToFilter, setMealSelectedToFilter] = useState<any[]>([])

  // Função para encontrar refeições incluídas
  const findMealIncluded = (list: any): void => {
    const data: string[] = []
    list.forEach((e: any) => {
      e.rates.forEach((each: Rates) => {
        const meal = each.mealIncluded.code
        if (!data.includes(meal)) data.push(meal)
      })
    })
    setMealIncluded(data)
    const dataToIncluded = data.map((e) => {
      return { value: false, code: e }
    })
    setMealSelectedToFilter(dataToIncluded)
  }

  // Função para aplicar filtros
  const applyFilters = (cb: () => void, list: any): void => {
    const storeFilterAfterAvail: IStoreFilterAfterAvail = {}
    let results: any[] = []
    let hasFilter = false

    if (hotelName) {
      hasFilter = true
      storeFilterAfterAvail.hotelName = hotelName
      const filterHotel = _.filter(list.availListAux, (item: any) => {
        const itemName = item.name.toLowerCase()
        return itemName.indexOf(hotelName.toLowerCase()) !== -1
      })
      results = filterHotel
    }

    if (hotelAddress) {
      hasFilter = true
      storeFilterAfterAvail.hotelAddress = hotelAddress
      const getHotelAddress = _.filter(
        results.length > 0 ? results : list.availListAux,
        (item: any) => {
          const itemAddress = item.location.address.toLowerCase()
          return itemAddress.indexOf(hotelAddress.toLowerCase()) !== -1
        },
      )
      results = getHotelAddress
    }

    if (filterOnlyAvailable) {
      hasFilter = true
      storeFilterAfterAvail.filterOnlyAvailable = filterOnlyAvailable

      const getFilterOnlyAvailable = _.filter(
        results.length > 0 ? results : list.availListAux,
        (item) => {
          return (
            item.roomTypes &&
            item.roomTypes.some((room: any) => room.availability !== 'NON')
          )
        },
      )

      results = getFilterOnlyAvailable.map((item) => ({
        ...item,
        roomTypes: availableRoomTypes(item.roomTypes).filter((roomType) =>
          item.roomTypes.some((room: any) => room.id === roomType.id),
        ),
      }))
    }

    if (propertyTypeId) {
      hasFilter = true
      storeFilterAfterAvail.propertyTypeId = propertyTypeId
      const filterByPropertyType = (
        results.length > 0 ? results : list.availListAux
      ).filter((hotel: Hotels) => {
        return hotel.propertyTypeId === propertyTypeId
      })

      results = filterByPropertyType
    }

    if (price) {
      hasFilter = true
      storeFilterAfterAvail.price = price
      const filterPrice = (
        results.length > 0 ? results : list.availListAux
      ).filter((item: any) => {
        return item.roomTypes.some((roomType: RoomType) => {
          return (roomType.averageRates ?? []).some(
            (averageRate: AverageRates) => {
              return (
                averageRate.amountBeforeTax >= price[0] &&
                averageRate.amountBeforeTax <= price[1]
              )
            },
          )
        })
      })

      results = filterPrice
    }

    if (selectedCodes.length > 0) {
      hasFilter = true
      storeFilterAfterAvail.selectedCodes = selectedCodes
      const filteredHotels = (
        results.length > 0 ? results : list.availListAux
      ).filter((hotel: Hotels) => {
        return hotel?.billings?.some((billing: Billing) =>
          selectedCodes.includes(billing.code),
        )
      })

      results = filteredHotels
    }

    if (selectedNeighborhoods.length > 0) {
      hasFilter = true
      storeFilterAfterAvail.selectedNeighborhoods = selectedNeighborhoods
      const filteredHotels = (
        results.length > 0 ? results : list.availListAux
      ).filter((hotel: Hotels) => {
        return selectedNeighborhoods.includes(hotel.location.neighborhood)
      })

      results = filteredHotels
    }

    if (selectedIntegratedSystems.length > 0) {
      hasFilter = true
      storeFilterAfterAvail.selectedIntegratedSystems =
        selectedIntegratedSystems
      const filteredHotels = (
        results.length > 0 ? results : list.availListAux
      ).filter((hotel: Hotels) => {
        return selectedIntegratedSystems.includes(hotel.systemId)
      })

      results = filteredHotels
    }

    if (mealSelectedToFilter.some((e: any) => e.value)) {
      hasFilter = true

      const mealSelected = mealSelectedToFilter
        .filter((e: any) => e.value)
        .map((e: any) => e.code)

      const filteredHotels = (
        results.length > 0 ? results : list.availListAux
      ).filter((hotel: Hotels) => {
        return hotel.rates.some((rate: Rates) =>
          mealSelected.includes(rate.mealIncluded.code),
        )
      })

      results = filteredHotels

      storeFilterAfterAvail.mealSelectedToFilter = mealSelectedToFilter
    }

    if (hotelChainSelected) {
      hasFilter = true
      storeFilterAfterAvail.hotelChainSelected = hotelChainSelected
      // Precisa incluir alphaId da rede de hoteis
    }

    CacheAdapter.set('storeFilterAfterAvail', storeFilterAfterAvail)
    list.setAvailList(hasFilter ? results : list.availListAux)
    CacheAdapter.set('availListStored', hasFilter ? results : list.availListAux)
    cb()
  }

  function cleanFilters(list: any, priceRange: any[]): void {
    list.setAvailList(list.availListAux)
    setPrice([priceRange[0], priceRange[1]])
    setHotelName('')
    setHotelAddress('')
    setSelectedNeighborhoods([])
    setSelectedIntegratedSystems([])
    setFilterOnlyAvailable(false)
    setSelectedCodes([])
    setHotelChainSelected(null)
    setWithBreakfast(false)
    setPropertyTypeId('')
    CacheAdapter.set('storeFilterAfterAvail', {})
    CacheAdapter.set('availListStored', [])
  }

  const availableRoomTypes = (roomTypes: RoomType[]): any[] => {
    return _.filter(roomTypes, (room) => room.availability !== 'NON')
  }

  const filterAndSortPropertyTypes = (availList: Hotels[]) => {
    const availablePropertyTypes = availList
      .map((hotel: Hotels) => hotel.propertyTypeId)
      .filter((typeId, index, self) => typeId && self.indexOf(typeId) === index)

    const sortedPropertyTypes = availablePropertyTypes.sort((a: any, b: any) =>
      a.localeCompare(b),
    )

    const propertyTypesList = sortedPropertyTypes.map((typeId) => ({
      id: typeId,
    }))

    setPropertyTypeList(propertyTypesList as any)

    if (propertyTypesList.length === 1)
      setPropertyTypeId(propertyTypesList[0].id as any)
  }

  const getAppliedFilter = (): void => {
    const storeFilterAfterAvail: IStoreFilterAfterAvail = CacheAdapter.get(
      'storeFilterAfterAvail',
    )
    if (storeFilterAfterAvail) {
      if (storeFilterAfterAvail.filterOnlyAvailable)
        setFilterOnlyAvailable(storeFilterAfterAvail.filterOnlyAvailable)
      if (storeFilterAfterAvail.hotelName)
        setHotelName(storeFilterAfterAvail.hotelName)
      if (storeFilterAfterAvail.hotelAddress)
        setHotelAddress(storeFilterAfterAvail.hotelAddress)
      if (storeFilterAfterAvail.propertyTypeId)
        setPropertyTypeId(storeFilterAfterAvail.propertyTypeId)
      if (storeFilterAfterAvail.selectedCodes)
        setSelectedCodes(storeFilterAfterAvail.selectedCodes)
      if (storeFilterAfterAvail.selectedNeighborhoods)
        setSelectedNeighborhoods(storeFilterAfterAvail.selectedNeighborhoods)
      if (storeFilterAfterAvail.selectedIntegratedSystems)
        setSelectedIntegratedSystems(
          storeFilterAfterAvail.selectedIntegratedSystems,
        )
      if (storeFilterAfterAvail.withBreakfast)
        setWithBreakfast(storeFilterAfterAvail.withBreakfast)
      if (storeFilterAfterAvail.price) setPrice(storeFilterAfterAvail.price)
      if (storeFilterAfterAvail.hotelChainSelected)
        setHotelChainSelected(storeFilterAfterAvail.hotelChainSelected)
      if (storeFilterAfterAvail.mealSelectedToFilter?.some((e: any) => e.value))
        setMealSelectedToFilter(storeFilterAfterAvail.mealSelectedToFilter)
    }
  }

  const billings = (hotels: Hotels[]) => {
    const codes = new Set()
    hotels.forEach((hotel: Hotels) => {
      hotel?.billings?.forEach((billing: Billing) => {
        codes.add(billing.code)
      })
    })

    return [...codes] as any[]
  }

  const systemIds = (hotels: Hotels[]) => {
    const systemIds = hotels.map((hotel: Hotels) => hotel.systemId)
    return _.uniq(systemIds)
  }

  return (
    <FilterContext.Provider
      value={{
        uniqueCodes,
        setUniqueCodes,
        integratedSystems,
        setIntegratedSystems,
        selectedIntegratedSystems,
        setSelectedIntegratedSystems,
        filterOnlyAvailable,
        setFilterOnlyAvailable,
        hotelName,
        setHotelName,
        price,
        setPrice,
        hotelAddress,
        setHotelAddress,
        selectedCodes,
        setSelectedCodes,
        selectedNeighborhoods,
        setSelectedNeighborhoods,
        withBreakfast,
        setWithBreakfast,
        hotelChainSelected,
        setHotelChainSelected,
        mealIncluded,
        setMealIncluded,
        propertyTypeId,
        setPropertyTypeId,
        propertyTypeList,
        setPropertyTypeList,
        mealSelectedToFilter,
        setMealSelectedToFilter,
        applyFilters,
        cleanFilters,
        findMealIncluded,
        handleChangePrice: (value: number | number[]) => setPrice(value as any),
        handleCheckboxChange: ({
          checked,
          value,
        }: {
          checked: boolean
          value: string
        }) => {
          setSelectedCodes((prevSelectedCodes: any) =>
            checked
              ? [...prevSelectedCodes, value]
              : prevSelectedCodes.filter((code: any) => code !== value),
          )
        },
        handleNeighborhoodsChange: ({
          checked,
          value,
        }: {
          checked: boolean
          value: string
        }) => {
          setSelectedNeighborhoods((prevSelectedNeighborhoods: any) =>
            checked
              ? [...prevSelectedNeighborhoods, value]
              : prevSelectedNeighborhoods.filter(
                  (neighborhood: any) => neighborhood !== value,
                ),
          )
        },
        handleIntegratedSystemsChange: ({
          checked,
          value,
        }: {
          checked: boolean
          value: string
        }) => {
          setSelectedIntegratedSystems((prevSelectedIntegratedSystems: any) =>
            checked
              ? [...prevSelectedIntegratedSystems, value]
              : prevSelectedIntegratedSystems.filter(
                  (integratedSystems: any) => integratedSystems !== value,
                ),
          )
        },
        filterAndSortPropertyTypes,
        getAppliedFilter,
        billings,
        systemIds,
      }}
    >
      {children}
    </FilterContext.Provider>
  )
}
